import cn from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { omit } from "lodash";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import AirlineSeatFlatRoundedIcon from "@mui/icons-material/AirlineSeatFlatRounded";
import AirlineStopsOutlinedIcon from "@mui/icons-material/AirlineStopsOutlined";
import AirplanemodeInactiveRoundedIcon from "@mui/icons-material/AirplanemodeInactiveRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import FlightLandRoundedIcon from "@mui/icons-material/FlightLandRounded";
import FlightRoundedIcon from "@mui/icons-material/FlightRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import GradeRoundedIcon from "@mui/icons-material/GradeRounded";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TimelapseRoundedIcon from "@mui/icons-material/TimelapseRounded";
import {
  Box,
  Button,
  InputAdornment,
  Modal,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { createAlertAction, getUsageAction, modifyAlertAction } from "@/api/alert";
import Airport from "@/components/Airport";
import ProChip from "@/components/ProChip";
import SearchRangeDate from "@/components/SearchBar/SearchRangeDate";
import SearchSelect from "@/components/SearchBar/SearchSelect";
import SelectPassengerV2 from "@/components/SelectPassengerV2";
import { DEFAULT_MAX_POINTS } from "@/constants";
import { AlertTypeEnum, cabinOptionMap, passengerTypeOptions } from "@/constants";
import { AIRLINE_NAME_MAP } from "@/constants/airline";
import { AirportMapItem } from "@/constants/airport";
import { AREA_CODES } from "@/constants/areaCode";
import { STOP_NUMBER_MAP } from "@/constants/filters";
import { PRO_ALERT_LIMIT } from "@/constants/membership";
import { PROGRAM_NAME_MAP, tranfer_bank_to_program } from "@/constants/programs";
import { TimePoints, TimePointsLabel } from "@/constants/timeRange";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import useSnackbar from "@/hooks/useSnackbar";
import useThemeData from "@/hooks/useThemeData";
import { transformerOptions } from "@/utils";

import { useAlertContext } from "./AlertContext";
import FormItem from "./FormItem";
import FormPhoneItem, { validatePhone } from "./FormPhoneItem";
import MileagePrograms from "./MileagePrograms";
import { GroupEnum, GroupItemOption } from "./MileagePrograms/useGroupOptions";
import Programs from "./Programs";

interface Option {
  label: string;
  value: string;
}

interface FromData {
  alert_type: string;
  alertId?: string;
  passengers: number;
  children: number;
  cabins: string;
  fromLocation: AirportMapItem[];
  toLocation: AirportMapItem[];
  departureDate: number;
  departureDateSec: number;
  stops: string;
  mileagePrograms: GroupItemOption[];
  banks: Option[];
  airlines: Option[];
  unwantedAirline: Option[];
  maxPoints: string;
  mixed_cabin: number | null;
  flightNumber: string;
  maxDuration: string | null;
  maxTax: string;
  departureTime: number[] | null;
  arrivalTime: number[] | null;
  infiniteMode: boolean;
  valid: boolean;
  areaCode: string;
  phoneNumber: string;
  agreeReceiveSMS: boolean;
}
export interface IRef {
  show: (data: FromData) => void;
  submit: () => void;
}

interface FlightAlertFormProps {
  onSuccess?: () => void;
  onReset?: () => void;
}

const FlightAlertForm = forwardRef<IRef, FlightAlertFormProps>((props, ref) => {
  const theme = useThemeData();
  const { isMobile } = usePlatform();
  const { enqueueSnackbar } = useSnackbar();

  const {
    setLoading,
    open,
    setOpen,
    setOpenResult,
    setAlertModalInfo,
    infiniteMode,
    setInfiniteMode,
  } = useAlertContext();

  const initDataUsedAlerts = useRef(0);
  const [usageLoading, setUsageLoading] = useState(true);

  const [alertNeeded, setAlertNeeded] = useState(-1);
  const [alertUsageInfo, setAlertUsageInfo] = useState({
    alertLimit: 0,
    alertUsed: 0,
    alertLeft: 0,
  });

  const [visible, setVisible] = useState(false);

  const store = useGlobalStoreContext();
  const navigate = useNavigate();

  const stopsOptions = useMemo(() => {
    return transformerOptions(STOP_NUMBER_MAP).map((option) => ({
      value: option.value,
      name: option.label,
    }));
  }, []);

  const airlineOptions = useMemo(() => {
    return transformerOptions(PROGRAM_NAME_MAP).map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const operatorProgramsOptions = useMemo(() => {
    return Object.keys(AIRLINE_NAME_MAP)
      .map((key) => ({
        value: key,
        label: AIRLINE_NAME_MAP[key],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const defaultFormData = useRef({
    alert_type: "flight",
    alertId: "",
    passengers: passengerTypeOptions[0].value,
    children: 0,
    cabins: cabinOptionMap[8].value,
    fromLocation: [],
    toLocation: [],
    departureDate: 0,
    departureDateSec: 0,
    stops: stopsOptions[0].value,
    banks: [],
    airlines: [],
    unwantedAirline: [],
    maxPoints: "",
    mixed_cabin: 80,
    flightNumber: "",
    maxTax: "",
    departureTime: null,
    arrivalTime: null,
    infiniteMode: false,
    valid: true,
    areaCode: AREA_CODES[0].dial_code,
    phoneNumber: "",
    agreeReceiveSMS: false,
    maxDuration: "",
    mileagePrograms: [],
  });

  const [formData, setFormData] = useState<FromData>(defaultFormData.current);

  const alertsInfo = useMemo(() => {
    let text = "";
    let color = theme.palette.success.main;
    let icon = <NotificationsActiveIcon sx={{ color }} fontSize="small" />;

    const { alertLeft } = alertUsageInfo;

    if (alertNeeded >= 0) {
      text += `${alertNeeded} active alerts needed, `;
    }

    if (alertLeft >= 0) {
      text += `${alertLeft} active alerts left`;
    }

    if (alertNeeded >= 0 && alertLeft >= 0) {
      if (alertNeeded > alertLeft) {
        color = theme.palette.error.main;
        icon = <ErrorOutlinedIcon sx={{ color }} fontSize="small" />;
      } else {
        icon = <CheckCircleOutlineIcon sx={{ color }} fontSize="small" />;
      }
    }

    return { text, color, icon };
  }, [alertUsageInfo, alertNeeded, theme.palette]);

  const handleCreate = useEvent(async () => {
    const maxPointsStr = (formData.maxPoints || "").trim();

    let message = "";

    if (!formData.fromLocation.length || !formData.toLocation.length) {
      message = "Airport cannot be empty.";
    } else if (!formData.departureDate || !formData.departureDateSec) {
      message = "Date cannot be empty.";
    } else if (formData.mileagePrograms.length == 0) {
      message = "Mileage Programs cannot be empty.";
    } else if (isNaN(Number(maxPointsStr))) {
      message = "Max Points per Passenger need to be a number.";
    } else if (formData.maxDuration && Number(formData.maxDuration) <= 0) {
      message = "Max duration hour cannot be zero or negative.";
    } else if (formData.maxTax && Number(formData.maxTax) <= 0) {
      message = "Max tax cannot be zero or negative.";
    }

    if (formData.phoneNumber && formData.areaCode) {
      const valid = validatePhone(formData);

      if (!valid) {
        message = "Invalid phone number.";
      }

      if (!formData.agreeReceiveSMS) {
        message = "Please agree to receive SMS based on your data";
      }
    }

    if (message) {
      enqueueSnackbar(message);
      return;
    }

    try {
      const params: any = {
        filter_criteria: {
          cabins: formData.cabins.split("&").map((item) => item.trim()),
          banks: formData.banks.map((ele) => ele.value || "All"),
          airline_programs: formData.airlines.map((ele) => ele.value || "All"),
          max_points: Number(formData.maxPoints.trim() || "999999999"),
          flight_number: formData.flightNumber || "",
          unwanted_airline: formData.unwantedAirline.map((ele) => ele.value),
          tax: formData.maxTax || "",
        },
        info: {
          passengers: formData.passengers,
          children: formData.children,
          departure: formData.fromLocation.map((ele) => ele.iata),
          arrival: formData.toLocation.map((ele) => ele.iata),
          departureDate: dayjs(formData.departureDate).format("YYYY-MM-DD"),
          departureDateSec: dayjs(formData.departureDateSec).format("YYYY-MM-DD"),
        },
        alert_type: AlertTypeEnum.Flight,
        infinite_mode: infiniteMode,
        phone_number: {
          number: formData.phoneNumber,
          area: formData.areaCode,
          agreeReceiveSMS: formData.agreeReceiveSMS,
        },
      };

      console.log("%c [  ]-286", "font-size:13px; background:pink; color:#bf2c9f;", params);

      if (formData.stops !== "" && Number(formData.stops) > -1) {
        params.filter_criteria.stops = formData.stops;
      }

      if (showMixedCabin && formData.mixed_cabin !== null) {
        params.filter_criteria.mixed_cabin = formData.mixed_cabin;
      }

      if (formData.departureTime !== null) {
        params.filter_criteria.departure_time = formData.departureTime;
      }

      if (formData.arrivalTime !== null) {
        params.filter_criteria.arrival_time = formData.arrivalTime;
      }

      if (formData.maxDuration !== null) {
        params.filter_criteria.max_duration = formData.maxDuration;
      }

      setLoading(true);

      if (formData.alertId) {
        await modifyAlertAction({ ...params, alert_id: formData.alertId });

        props.onSuccess && props.onSuccess();
        setOpen(false);
      } else {
        await createAlertAction(params);

        setAlertModalInfo({
          alertType: AlertTypeEnum.Flight,
          isSuccess: true,
          fromLocation: formData.fromLocation,
          toLocation: formData.toLocation,
          departureDate: formData.departureDate,
          departureDateSec: formData.departureDateSec,
        });

        props.onSuccess && props.onSuccess();

        setOpen(false);
        setOpenResult(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.indexOf("Exceed alert limit") !== -1) {
          setAlertModalInfo({
            isSuccess: false,
            alertLimit: alertUsageInfo.alertLimit,
          });

          setOpen(false);
          setOpenResult(true);
        } else {
          enqueueSnackbar(error.message || "");
        }
      }
    } finally {
      setLoading(false);
    }
  });

  const showMixedCabin = useMemo(() => {
    return (
      formData.cabins &&
      (formData.cabins.split("&").includes("First") ||
        formData.cabins.split("&").includes("Business"))
    );
  }, [formData.cabins]);

  const handleAirportChange = useEvent(
    ({
      fromLocation,
      toLocation,
    }: {
      fromLocation: AirportMapItem[];
      toLocation: AirportMapItem[];
    }) => {
      setFormData({ ...formData, fromLocation, toLocation });
    }
  );

  const handleDepartureDateChange = useEvent((date: Array<Dayjs | null>) => {
    setFormData({
      ...formData,
      departureDate: date[0] ? dayjs(date[0]).valueOf() : 0,
      departureDateSec: date[1] ? dayjs(date[1]).valueOf() : 0,
    });
  });

  const handleDepartureTimeChange = useEvent(
    (event: Event, newValue: number | number[], activeThumb: number) => {
      if (!Array.isArray(newValue)) return;

      if (activeThumb === 0) {
        setFormData((pre) => ({
          ...formData,
          departureTime: [newValue[0], pre.departureTime ? pre.departureTime[1] : 24],
        }));
      } else {
        setFormData((pre) => ({
          ...formData,
          departureTime: [pre.departureTime ? pre.departureTime[0] : 0, newValue[1]],
        }));
      }
    }
  );

  const handleArrivalTimeChange = useEvent(
    (event: Event, newValue: number | number[], activeThumb: number) => {
      if (!Array.isArray(newValue)) return;

      if (activeThumb === 0) {
        setFormData((pre) => ({
          ...formData,
          arrivalTime: [newValue[0], pre.arrivalTime ? pre.arrivalTime[1] : 24],
        }));
      } else {
        setFormData((pre) => ({
          ...formData,
          arrivalTime: [pre.arrivalTime ? pre.arrivalTime[0] : 0, newValue[1]],
        }));
      }
    }
  );

  const handleMileageProgramsChange = (data: GroupItemOption[]) => {
    const preBanks = formData.banks;
    const banks = data.filter((ele) => ele.type === GroupEnum.Card);
    let airlines = data.filter((ele) => ele.type === GroupEnum.Airline);

    const additionalBanks = banks.filter(
      (ele) => !preBanks.find((item) => item.value === ele.value)
    );

    if (additionalBanks.length) {
      let programs: string[] = [];

      additionalBanks.forEach((bank) => {
        programs = programs.concat(tranfer_bank_to_program.get(bank.label) || []);
      });

      programs = [...new Set(programs)];

      if (programs.length) {
        programs = [...programs, ...airlines.map((ele) => ele.value)];
        programs = [...new Set(programs)];
        airlines = airlineOptions
          .filter((ele) => programs.includes(ele.value))
          .map((item) => ({ ...item, type: GroupEnum.Airline }));
      }
    }

    setFormData({
      ...formData,
      mileagePrograms: [...airlines],
      banks: banks.map((item) => omit(item, "type")),
      airlines: airlines.map((item) => omit(item, "type")),
    });
  };

  const handleOperatorProgramsChange = useEvent((data: Option[]) => {
    setFormData({
      ...formData,
      unwantedAirline: data.length === 0 ? [] : data,
    });
  });

  const formatLabel = useCallback((value: number) => TimePointsLabel[TimePoints[value]], []);

  const promptUpsellUI = () => {
    return store?.show_upsell ? (
      <Typography
        component={Button}
        variant="body2"
        color="primary"
        onClick={() => {
          window.open("/pro", "_blank", "noopener,noreferrer");
        }}
        sx={{ paddingLeft: "10px", marginLeft: "10px", lineHeight: "16px" }}
      >
        <div className="mr-[6px]">
          <ProChip height="18px" />
        </div>
        Get {PRO_ALERT_LIMIT} alerts now!
      </Typography>
    ) : null;
  };

  const getUsage = useEvent(() => {
    setUsageLoading(true);
    getUsageAction()
      .then((res) => {
        const alertLeft = res.max_alert - res.alert_set;

        setAlertUsageInfo({
          alertLimit: res.max_alert,
          alertUsed: res.alert_set,
          alertLeft: alertLeft + initDataUsedAlerts.current,
        });
        setUsageLoading(false);
      })
      .catch(() => {
        setUsageLoading(false);
      });
  });

  const handleShow = (data: FromData) => {
    if (data) {
      console.log("%c [  ]-496", "font-size:13px; background:pink; color:#bf2c9f;", data);
      const newData = {
        ...defaultFormData.current,
        ...(data || {}),
      };

      if (data.airlines?.length || data.banks?.length) {
        newData.mileagePrograms = [
          ...data.airlines.map((item) => ({ ...item, type: GroupEnum.Airline })),
          // ...data.banks.map((item) => ({ ...item, type: GroupEnum.Card })),
        ];
      }

      setFormData(newData);

      setInfiniteMode(data.infiniteMode || false);

      if (data.alertId && data.valid) {
        const usedAlerts =
          (dayjs(data.departureDateSec).startOf("day").diff(data.departureDate, "day") + 1) *
          data.toLocation.length *
          data.fromLocation.length;

        initDataUsedAlerts.current = usedAlerts;
      }
      setOpen(true);
    }
  };

  const handleClose = useEvent(() => {
    props.onReset && props.onReset();
  });

  useEffect(() => {
    if (open) {
      getUsage();
    } else {
      setTimeout(() => {
        setFormData(defaultFormData.current);
        setAlertUsageInfo({ alertLimit: 0, alertUsed: 0, alertLeft: -1 });
        setAlertNeeded(-1);
        // setInitDataUsedAlerts(0);
        handleClose();
      }, 500);
    }
  }, [open, getUsage, handleClose]);

  useEffect(() => {
    if (
      formData.departureDate &&
      formData.departureDateSec &&
      formData.fromLocation.length &&
      formData.toLocation.length
    ) {
      const count =
        (dayjs(formData.departureDateSec).startOf("day").diff(formData.departureDate, "day") + 1) *
        formData.fromLocation.length *
        formData.toLocation.length;

      setAlertNeeded(count);
    } else {
      setAlertNeeded(-1);
    }
  }, [
    formData.departureDate,
    formData.departureDateSec,
    formData.fromLocation,
    formData.toLocation,
  ]);

  const renderContent = () => {
    return (
      <>
        <Box className="flex items-center mb-[10px]">
          <SelectPassengerV2
            min={passengerTypeOptions[0].value}
            max={passengerTypeOptions[passengerTypeOptions.length - 1].value}
            value={formData.passengers}
            onChange={(data) => {
              setFormData({ ...formData, passengers: data });
            }}
            childValue={formData.children}
            onChangeChild={(data) => {
              setFormData({ ...formData, children: data });
            }}
          />

          <SearchSelect
            value={formData.cabins}
            options={cabinOptionMap}
            onChange={(data) => {
              setFormData({ ...formData, cabins: data });
            }}
          />
        </Box>

        <Airport
          fromLocation={formData.fromLocation}
          toLocation={formData.toLocation}
          onChange={handleAirportChange}
        />

        <Box className="mt-[16px]">
          <SearchRangeDate
            label=""
            defaultValue={[
              formData.departureDate ? dayjs(formData.departureDate) : null,
              formData.departureDateSec ? dayjs(formData.departureDateSec) : null,
            ]}
            onChange={handleDepartureDateChange}
          />
        </Box>

        {isMobile ? promptUpsellUI() : null}
        {/* <Typography color="primary" variant="body2">Upgrade to PRO </Typography> */}
        {/* TODO: BRING BACK PHONE NUMBER */}
        <Box
          onClick={() => {
            if (store.show_upsell) {
              setVisible(true);
              return false;
            }
          }}
          className="mt-[16px]"
        >
          <FormPhoneItem
            formData={formData}
            show_upsell={store.show_upsell}
            setFormData={(value) => setFormData({ ...formData, ...value })}
          />
        </Box>
        {!usageLoading && alertsInfo.text ? (
          <Box
            className="mb-[0px] mt-[-6px] flex items-center justify-center"
            sx={{ maxHeight: "12px" }}
          >
            {alertsInfo.icon}
            <Typography
              variant="body2"
              sx={{ color: alertsInfo.color, paddingLeft: "5px", lineHeight: "6px" }}
            >
              {alertsInfo.text}
            </Typography>
            {!isMobile ? promptUpsellUI() : null}
          </Box>
        ) : (
          <div />
        )}

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
          Required
        </Typography>

        <FormItem
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CreditCardRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Mileage Programs:
            </Box>
          }
          labelClass="h-[48px] min-w-[110px] self-start"
        >
          <MileagePrograms
            selectData={formData.mileagePrograms}
            onChange={handleMileageProgramsChange}
            placeholder="Select Programs"
            maxCount={Infinity}
          />
        </FormItem>
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
          Optional
        </Typography>
        <FormItem
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <GradeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Max Points per Passenger:
            </Box>
          }
        >
          <TextField
            placeholder="Any"
            variant="outlined"
            size="small"
            type="number"
            value={formData.maxPoints}
            onChange={(e) => {
              setFormData({ ...formData, maxPoints: e.target.value });
            }}
            inputProps={{ style: { fontSize: "14px", width: "80px" }, min: 0 }}
          />
        </FormItem>
        <div
          onClick={() => {
            if (store.show_upsell) {
              setVisible(true);
              return false;
            }
          }}
        >
          <FormItem
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AirlineStopsOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                Stops:
              </Box>
            }
          >
            <div className="flex items-center">
              <SearchSelect
                value={formData.stops}
                options={stopsOptions}
                onChange={(data) => {
                  setFormData({ ...formData, stops: data });
                }}
                onBefore={() => {
                  if (store.show_upsell) {
                    setVisible(true);
                    return false;
                  }

                  return true;
                }}
              />
              <ProChip />
            </div>
          </FormItem>
        </div>
        <div
          onClick={() => {
            if (store.show_upsell) {
              setVisible(true);
              return false;
            }
          }}
        >
          <FormItem
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MonetizationOnOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                Max Tax per Passenger:
              </Box>
            }
            labelClass="min-w-[100px]"
          >
            <TextField
              placeholder="Any"
              variant="outlined"
              type="number"
              size="small"
              value={formData.maxTax}
              onChange={(e) => {
                setFormData({ ...formData, maxTax: e.target.value });
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { min: 0 },
              }}
              inputProps={{ style: { fontSize: "14px", width: "80px" }, placeholder: "any" }}
            />
            <div className="flex items-center">
              <ProChip />
            </div>
          </FormItem>
        </div>
        {showMixedCabin ? (
          <div className="mt-[48px]">
            <FormItem
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AirlineSeatFlatRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Premium Cabin Percentage:
                </Box>
              }
            >
              <div className={cn("flex flex-1 mx-[20px]")}>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  valueLabelFormat={(value: number) => `${value}%`}
                  valueLabelDisplay="on"
                  value={formData.mixed_cabin || 0}
                  onChange={(_event: Event, val: number | number[]) => {
                    setFormData({ ...formData, mixed_cabin: val as number });
                  }}
                />
              </div>
            </FormItem>
          </div>
        ) : null}
        <FormItem
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AirplanemodeInactiveRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Airline(s) To Exclude:
            </Box>
          }
          labelClass="h-[48px] min-w-[110px] self-start"
        >
          <Programs
            size="small"
            title="Excluded Airline(s)"
            options={operatorProgramsOptions}
            selectOptions={formData.unwantedAirline}
            onChange={handleOperatorProgramsChange}
            placeholder="Airline(s) To Exclude"
          />
        </FormItem>
        <FormItem
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FlightRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Flight Number:
            </Box>
          }
          labelClass="min-w-[100px]"
        >
          <Tooltip
            title={
              "Receive notifications when the price of your desired flight falls below a specific price. If your trip includes multiple flight, please specify any flight number."
            }
          >
            <TextField
              placeholder="Any"
              variant="outlined"
              size="small"
              value={formData.flightNumber}
              onChange={(e) => {
                setFormData({ ...formData, flightNumber: e.target.value });
              }}
              inputProps={{ style: { fontSize: "14px", width: "100px" }, placeholder: "Any" }}
            />
          </Tooltip>
        </FormItem>

        <FormItem
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TimelapseRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Max Duration (in hour):
            </Box>
          }
          labelClass="min-w-[100px]"
        >
          <TextField
            variant="outlined"
            type="number"
            size="small"
            value={formData.maxDuration}
            placeholder="Any"
            onChange={(e) => {
              setFormData({ ...formData, maxDuration: e.target.value });
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start" />,
              inputProps: { min: 0 },
            }}
            inputProps={{ style: { fontSize: "14px", width: "80px" }, placeholder: "Any" }}
          />
        </FormItem>
        <div className="mt-[48px]">
          <FormItem
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FlightTakeoffRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                Departure Time:
              </Box>
            }
          >
            <div className={cn("flex flex-1 mx-[30px]")}>
              <Slider
                min={0}
                max={24}
                disableSwap
                valueLabelDisplay="on"
                getAriaValueText={formatLabel}
                valueLabelFormat={formatLabel}
                value={formData.departureTime === null ? [0, 24] : formData.departureTime}
                onChange={handleDepartureTimeChange}
              />
            </div>
          </FormItem>
        </div>

        <div className="mt-[48px]">
          <FormItem
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FlightLandRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
                Arrival Time:
              </Box>
            }
          >
            <div className={cn("flex flex-1 mx-[30px]")}>
              <Slider
                min={0}
                max={24}
                disableSwap
                valueLabelDisplay="on"
                getAriaValueText={formatLabel}
                valueLabelFormat={formatLabel}
                value={formData.arrivalTime === null ? [0, 24] : formData.arrivalTime}
                onChange={handleArrivalTimeChange}
              />
            </div>
          </FormItem>
        </div>
      </>
    );
  };

  useImperativeHandle(ref, () => ({
    show: handleShow,
    submit: handleCreate,
  }));

  return (
    <div>
      {isMobile ? renderContent() : renderContent()}
      <Modal open={visible} onClose={() => setVisible(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? 300 : 400,
            bgcolor: "background.paper",
            p: 4,
            boxShadow: 2,
            borderRadius: "12px",
          }}
        >
          <Typography color="textPrimary">
            <ProChip />
            {
              " Feature - Upgrade now to unlock the most advanced award alerts, never miss a deal again."
            }
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.open("/pro", "_blank")}
              sx={{ borderRadius: "12px" }}
            >
              Upgrade Now! 🚀
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
});

FlightAlertForm.displayName = "FlightAlertForm";

export default FlightAlertForm;
